import { DOMUtil } from "@mcleod/core";
import { Component } from "../..";
import { Label } from "./Label";
import { Panel } from "../panel/Panel";

export class LabelSizer {
    private fullText: string;
    private label: Label;
    private panel: Panel;

    constructor(panel: Panel, label: Label) {
        this.panel = panel;
        this.label = label;
        this.fullText = this.label?.text;
    }

    public sizeForCurrentWidth() {
        this.size(false);
    }

    public sizeForMaxWidth() {
        this.size(true);
    }

    private size(includeMaxWidth: boolean) {
        if (this.isResizeInProgressInPanel() || this.label == null) {
            return;
        }
        try {
            this.markResizeInProgressInPanel(true);
            this.panel.doWhileDimensionsComputed(() => {
                const panelMaxWidth = DOMUtil.getComputedStyleAsNumber("max-width", this.panel._element);
                if (includeMaxWidth) {
                    if (panelMaxWidth === 0) {
                        this.label.maxWidth = null;
                        return;
                    }
                }
                const otherComponents: Component[] = this.panel.components.filter(component => {
                    // Filter out HorizontalSpacers since they would take up all of the free space while measuring
                    return component !== this.label && component.serializationName !== "horizontalspacer";
                });
                const otherElements: Element[] = otherComponents.map(component => component._element);
                let widthAvailableForLabel: number;
                // Remove the label from the panel temporarily so that we can measure the other elements without them being
                // squeezed by the label
                const labelIndex = this.panel.indexOf(this.label);
                this.panel.remove(this.label);
                this.panel.doWhileDimensionsComputed(() => {
                    widthAvailableForLabel = DOMUtil.getAvailableWidth(this.panel._element, otherElements);
                    if (includeMaxWidth) {
                        const panelWidth = DOMUtil.getElementWidth(this.panel._element);
                        if (!isNaN(panelMaxWidth) && panelMaxWidth > panelWidth) {
                            widthAvailableForLabel += (panelMaxWidth - panelWidth);
                        }
                    }
                });
                // Apply the calculated width to the label
                if (widthAvailableForLabel > 0) {
                    this.label.maxWidth = widthAvailableForLabel;
                }
                // Re-insert the label
                this.panel.insert(this.label, labelIndex);
                this.setLabelTooltipIfOverflowing();
            });
        } finally {
            this.markResizeInProgressInPanel(false);
        }
    }

    /**
     * If the tab's heading label is too long for its width, set the label's tooltip to the full label text.
     * Otherwise clear the tooltip.
     */
    private setLabelTooltipIfOverflowing() {
        if (DOMUtil.isElementOverflowingX(this.label._element)) {
            if (this.label.tooltip == null) {
                this.label.tooltip = this.fullText;
            }
        } else if (this.label.tooltip === this.fullText) {
            this.label.tooltip = null;
        }
    }

    // Stashes a value in the panel to indicate a label resize is in progress.  This way, if another LabelSizer tries to
    // size the panel at the same time, it can see a resize is in progress and stop.
    private markResizeInProgressInPanel(value: boolean) {
        if (value) {
            this.panel["__labelResizeInProgress"] = true;
        } else {
            delete this.panel["__labelResizeInProgress"];
        }
    }

    private isResizeInProgressInPanel(): boolean {
        return (this.panel["__labelResizeInProgress"] ?? false) === true;
    }
}
